/**
 * ReceiptOrderDetailModal Component
 * Admin-only modal to view order details including table of powers
 * Allows admin to confirm receipt on behalf of an agency
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { OathModal, OathButton, OathDualSpinner, OathEmptyState, OathAlert } from '../../../shared/OathUI';
import { getReceiptOrderDetails } from '../../../../services/oathPowerReportService';
import { formatDate, getPowerStatusBadgeInfo } from '../utils/formatters';

const getStatusAccentBar = (statusCode) => {
  switch (statusCode?.toUpperCase()) {
    case 'E': return 'bg-green-500';
    case 'T': return 'bg-teal-500';
    case 'U': return 'bg-blue-500';
    case 'UT': return 'bg-amber-500';
    case 'V': return 'bg-red-500';
    case 'F':
    case 'UF': return 'bg-orange-500';
    case 'J':
    case 'UJ': return 'bg-purple-500';
    case 'D':
    case 'UD': return 'bg-gray-400';
    default: return 'bg-gray-300 dark:bg-gray-600';
  }
};

const ReceiptOrderDetailModal = ({
  isOpen,
  onClose,
  orderId,
  agentId,
  agencyName,
  onConfirmClick
}) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Load order details when modal opens
  const loadOrderDetails = useCallback(async () => {
    if (!orderId) return;

    setLoading(true);
    setError('');

    try {
      const result = await getReceiptOrderDetails(orderId);

      if (result.success) {
        setOrderDetails(result.data);
      } else {
        setError(result.error || 'Failed to load order details');
      }
    } catch (err) {
      console.error('Error loading order details:', err);
      setError('Network error loading order details');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  // Load data when modal opens
  useEffect(() => {
    if (isOpen && orderId) {
      loadOrderDetails();
    }
  }, [isOpen, orderId, loadOrderDetails]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOrderDetails(null);
      setError('');
      setSearchTerm('');
    }
  }, [isOpen]);

  // Filter powers by search term (power number substring)
  const filteredPowers = useMemo(() => {
    if (!orderDetails?.powers?.length) return [];
    const q = searchTerm.trim().toLowerCase();
    if (!q) return orderDetails.powers;
    return orderDetails.powers.filter((p) =>
      (p.powerNumber ?? '').toString().toLowerCase().includes(q)
    );
  }, [orderDetails?.powers, searchTerm]);

  // Tally powers by status for the breakdown row
  const statusBreakdown = useMemo(() => {
    if (!orderDetails?.powers?.length) return [];
    const counts = new Map();
    orderDetails.powers.forEach((p) => {
      const key = (p.status || '?').toUpperCase();
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([code, count]) => ({ code, count, info: getPowerStatusBadgeInfo(code) }))
      .sort((a, b) => b.count - a.count);
  }, [orderDetails?.powers]);

  // Handle confirm button click
  const handleConfirmClick = () => {
    onConfirmClick?.({
      orderId,
      agentId,
      agencyName
    });
    onClose();
  };

  return (
    <OathModal
      isOpen={isOpen}
      onClose={onClose}
      title="Order Details"
      size="xl"
      className="!max-h-[85vh]"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            Close
          </OathButton>
          <OathButton
            onClick={handleConfirmClick}
            icon={CheckCircleIcon}
            disabled={loading || !!error}
            className="w-full sm:w-auto"
          >
            Confirm Receipt
          </OathButton>
        </div>
      }
    >
      <div className="h-full flex flex-col gap-3">
        {/* Agency + order metadata with shipment status pills */}
        <div className="shrink-0 flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <div className="flex items-center gap-2 min-w-0">
            <BuildingOfficeIcon className="h-5 w-5 text-primary dark:text-primary-light shrink-0" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
              {agencyName || 'Unknown Agency'}
              {agentId != null && (
                <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">
                  ({agentId})
                </span>
              )}
            </h3>
          </div>
          {orderDetails?.orderNumber && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Order <span className="font-medium text-gray-900 dark:text-white">#{orderDetails.orderNumber}</span>
            </span>
          )}
          {orderDetails?.trackingNumber && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              Tracking <span className="font-medium text-gray-900 dark:text-white">{orderDetails.trackingNumber}</span>
            </span>
          )}
          {orderDetails && (
            <div className="ml-auto flex items-center gap-1.5">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                <CheckCircleIcon className="h-3.5 w-3.5" />
                Assigned {orderDetails.shipDate ? formatDate(orderDetails.shipDate) : '—'}
              </span>
              {orderDetails.dateReceived ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  Received {formatDate(orderDetails.dateReceived)}
                  {orderDetails.receivedBy && <span className="ml-0.5">by {orderDetails.receivedBy}</span>}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                  <ClockIcon className="h-3.5 w-3.5" />
                  Awaiting Confirmation
                </span>
              )}
            </div>
          )}
        </div>

        {/* Thin gradient divider */}
        <div className="shrink-0 h-px bg-gradient-to-r from-primary/40 via-gray-200 to-transparent dark:via-gray-700" />

        {/* Content body — flex-1 fills remaining space */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <OathDualSpinner size="lg" />
          </div>
        ) : error ? (
          <OathAlert variant="danger">{error}</OathAlert>
        ) : !orderDetails?.powers?.length ? (
          <div className="flex-1 flex items-center justify-center">
            <OathEmptyState
              icon={DocumentTextIcon}
              title="No Powers Found"
              message="No power details available for this order."
            />
          </div>
        ) : (
          <>
            {/* Heading + inline search + status breakdown tallies */}
            <div className="shrink-0 flex flex-wrap items-center gap-2">
              <h4 className="text-sm font-semibold text-primary dark:text-primary-light whitespace-nowrap">
                Powers in Shipment{' '}
                <span className="font-normal text-gray-500 dark:text-gray-400">
                  {searchTerm.trim()
                    ? `(${filteredPowers.length} of ${orderDetails.powers.length})`
                    : `(${orderDetails.powers.length})`}
                </span>
              </h4>

              <div className="relative flex-1 min-w-[9rem] max-w-xs">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search power #"
                  className="w-full pl-7 pr-7 py-1 text-xs bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    aria-label="Clear search"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-wrap ml-auto">
                {statusBreakdown.map(({ code, count, info }) => (
                  <span key={code} className={info.classes}>
                    <span className="font-semibold tabular-nums">{count}</span>
                    <span className="ml-1 opacity-80">{info.label}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Powers grid — only scroll area in the modal */}
            <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
              {filteredPowers.length === 0 ? (
                <div className="h-full flex items-center justify-center py-10 text-sm text-gray-500 dark:text-gray-400">
                  No powers match &quot;{searchTerm}&quot;
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 dark:bg-gray-700">
                  {filteredPowers.map((power, index) => {
                    const statusInfo = getPowerStatusBadgeInfo(power.status);
                    return (
                      <div
                        key={power.powerId || index}
                        className="relative flex items-center justify-between gap-2 pl-4 pr-2.5 py-1.5 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors"
                        title={power.statusDescription || statusInfo.label}
                      >
                        <span className={`absolute left-0 top-1 bottom-1 w-1 rounded-r-sm ${getStatusAccentBar(power.status)}`} />
                        <span className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {power.powerNumber}
                        </span>
                        <span className={`${statusInfo.classes} whitespace-nowrap`}>
                          {statusInfo.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </OathModal>
  );
};

export default ReceiptOrderDetailModal;
